<template>
  <v-snackbar
    v-model="notification.snackbar"
    class="app-notification"
    :timeout="timeout"
    location="bottom center"
    @update:model-value="onClose($event)"
  >
    <template #default>
      <div class="notification-card rounded-lg">
        <v-btn
          icon="close"
          variant="text"
          density="compact"
          size="x-small"
          class="notification-close"
          @click="notification.dismiss()"
        />
        <div class="notification-row d-flex align-center ga-3 pl-4 pr-8 py-3">
          <v-icon
            :icon="notificationIcon"
            :color="displayed?.color"
            size="20"
          />
          <div class="notification-text text-body-2">
            <span>{{ displayed?.text ?? '' }}</span>
            <span v-if="displayed?.detail" class="notification-detail">
              {{ displayed.detail }}
            </span>
          </div>
          <v-btn
            v-if="displayed?.action"
            variant="outlined"
            size="small"
            class="notification-action"
            @click="runAction()"
          >
            {{ displayed.action.label }}
          </v-btn>
        </div>
        <div
          :key="progressKey"
          class="notification-progress"
          :class="{ 'notification-progress--sweep': progressMs === null }"
          :style="progressStyle"
        />
      </div>
    </template>
  </v-snackbar>
</template>

<script lang="ts" setup>
  import { computed, ref, watch } from 'vue';
  import { useNotificationStore } from '@/stores/notification';
  import type { NotificationItem } from '@/types/shared/notification';

  const TIMEOUT_MS = 3000;

  const notification = useNotificationStore();

  /**
   * The toast being drawn, which is not always the one the store calls
   * current. Setting `snackbar` false only *starts* v-snackbar's exit
   * animation, and the store clears `current` on that same tick — so for the
   * length of the fade the card is still on screen with nothing behind it,
   * and every binding below falls back at once: no text, the default info
   * icon, and an info-coloured bar. Holding the last real item keeps the
   * toast intact until it has finished leaving.
   */
  const displayed = ref<NotificationItem | null>(notification.current);
  watch(
    () => notification.current,
    item => {
      if (item) displayed.value = item;
    }
  );

  const iconMap: Record<string, string> = {
    success: 'check_circle',
    error: 'error',
    warning: 'warning'
  };

  const notificationIcon = computed(
    () =>
      displayed.value?.icon ?? iconMap[displayed.value?.color ?? ''] ?? 'info'
  );

  /** -1 disables v-snackbar's auto-close, leaving dismissal to the caller. */
  const timeout = computed(() =>
    displayed.value?.persistent ? -1 : TIMEOUT_MS
  );

  /** Countdown duration, or null when there is no known end to count towards. */
  const progressMs = computed(() => {
    const current = displayed.value;
    if (current?.progressMs !== undefined) return current.progressMs;
    return current?.persistent ? null : TIMEOUT_MS;
  });

  const progressStyle = computed(() => {
    const ms = progressMs.value;
    const color = displayed.value?.color ?? 'info';
    return {
      background: `rgb(var(--v-theme-${color}))`,
      // The sweep carries its own duration; setting one inline would win over it.
      ...(ms === null ? {} : { animationDuration: `${ms}ms` })
    };
  });

  /** Remounts the line so its animation restarts on a new toast or new phase. */
  const progressKey = computed(
    () => `${displayed.value?.id ?? ''}:${progressMs.value ?? 'sweep'}`
  );

  function runAction() {
    const action = displayed.value?.action;
    notification.dismiss();
    action?.handler();
  }

  function onClose(open: boolean) {
    if (open === false) {
      notification.dismiss();
    }
  }
</script>

<style scoped>
  :deep(.v-snackbar__wrapper) {
    background: transparent !important;
    box-shadow: none !important;
    padding: 0;
  }

  :deep(.v-snackbar__content) {
    padding: 0 !important;
  }

  .notification-card {
    position: relative;
    overflow: hidden;
    min-width: 320px;
    background: rgb(var(--v-theme-surface));
    border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }

  /**
   * Held at the height the action button gives the row (28px for size="small",
   * plus the 24px of py-3), so the toast keeps its size when the button comes
   * off at the end of the cancel window.
   */
  .notification-row {
    min-height: 52px;
  }

  .notification-text {
    flex: 1 1 auto;
    color: rgb(var(--v-theme-on-surface));
  }

  .notification-detail {
    margin-left: 6px;
    opacity: 0.6;
  }

  .notification-action {
    flex: 0 0 auto;
    color: rgb(var(--v-theme-on-surface));
    border-color: rgba(var(--v-theme-on-surface), 0.24);
    text-transform: none;
    letter-spacing: normal;
  }

  .notification-close {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 1;
    color: rgb(var(--v-theme-on-surface));
  }

  .notification-close :deep(.v-icon) {
    font-size: 14px;
  }

  .notification-progress {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 2px;
    width: 100%;
    transform-origin: left;
    animation: notification-countdown linear forwards;
  }

  /** Work with no known end: keep moving rather than counting towards zero. */
  .notification-progress--sweep {
    width: 40%;
    animation: notification-sweep 1.2s ease-in-out infinite;
  }

  @keyframes notification-countdown {
    from {
      transform: scaleX(1);
    }
    to {
      transform: scaleX(0);
    }
  }

  @keyframes notification-sweep {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(250%);
    }
  }
</style>
